const TEMPLATES_KEY = "nettles_saved_templates_v1";
const CHATS_KEY = "nettles_chat_threads_v1";
const ACTIVE_CHAT_KEY = "nettles_active_chat_id";
const EVE_HOST_KEY = "nettles_eve_host";
const API_KEY_KEY = "nettles_ai_key";
const PROVIDER_KEY = "nettles_ai_provider";
const ANTHROPIC_MODEL_KEY = "nettles_anthropic_model";
const MODEL_CATALOG_KEY = "nettles_model_catalog_v1";

export function getEveHost() {
  const v = localStorage.getItem(EVE_HOST_KEY);
  // Empty string = same-origin CRA proxy (/eve → Eve :2000)
  return v === null ? "" : v;
}

export function setEveHost(host) {
  localStorage.setItem(EVE_HOST_KEY, host ?? "");
}

export function getApiKey() {
  return localStorage.getItem(API_KEY_KEY) || "";
}

export function getProvider() {
  return "anthropic";
}

export function getAnthropicModel() {
  return localStorage.getItem(ANTHROPIC_MODEL_KEY) || "claude-sonnet-5";
}

export function loadModelCatalog() {
  try {
    const raw = localStorage.getItem(MODEL_CATALOG_KEY);
    const parsed = raw ? JSON.parse(raw) : {};
    return {
      anthropic: Array.isArray(parsed.anthropic) ? parsed.anthropic : [],
      at: Number(parsed.at) || 0,
    };
  } catch {
    return { anthropic: [], at: 0 };
  }
}

export function saveModelCatalog(catalog) {
  const prev = loadModelCatalog();
  const next = {
    anthropic: catalog.anthropic || prev.anthropic,
    at: Date.now(),
  };
  localStorage.setItem(MODEL_CATALOG_KEY, JSON.stringify(next));
  return next;
}

export function saveAiCredentials({ apiKey, provider, anthropicModel }) {
  if (typeof apiKey === "string") localStorage.setItem(API_KEY_KEY, apiKey.trim());
  localStorage.setItem(PROVIDER_KEY, provider || "anthropic");
  if (anthropicModel) localStorage.setItem(ANTHROPIC_MODEL_KEY, anthropicModel);
}

/** @returns {{ id: string, name: string, html: string, createdAt: string, source?: string }[]} */
export function loadSavedTemplates() {
  try {
    const raw = localStorage.getItem(TEMPLATES_KEY);
    const list = raw ? JSON.parse(raw) : [];
    return Array.isArray(list) ? list : [];
  } catch {
    return [];
  }
}

export function saveTemplateRecord({ name, html, source = "user" }) {
  const list = loadSavedTemplates();
  const id = `tpl_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`;
  const record = {
    id,
    name: (name || "Untitled template").trim().slice(0, 80),
    html,
    source,
    createdAt: new Date().toISOString(),
  };
  list.unshift(record);
  localStorage.setItem(TEMPLATES_KEY, JSON.stringify(list.slice(0, 40)));
  return record;
}

export function deleteTemplateRecord(id) {
  const list = loadSavedTemplates().filter((t) => t.id !== id);
  localStorage.setItem(TEMPLATES_KEY, JSON.stringify(list));
  return list;
}

export function getTemplateById(id) {
  return loadSavedTemplates().find((t) => t.id === id) || null;
}

/** @returns {{ id: string, title: string, messages: object[], sessionId?: string|null, updatedAt: string }[]} */
export function loadChatThreads() {
  try {
    const raw = localStorage.getItem(CHATS_KEY);
    const list = raw ? JSON.parse(raw) : [];
    return Array.isArray(list) ? list : [];
  } catch {
    return [];
  }
}

export function getActiveChatId() {
  return localStorage.getItem(ACTIVE_CHAT_KEY) || null;
}

export function setActiveChatId(id) {
  if (id) localStorage.setItem(ACTIVE_CHAT_KEY, id);
  else localStorage.removeItem(ACTIVE_CHAT_KEY);
}

export function upsertChatThread(thread) {
  const list = loadChatThreads();
  const idx = list.findIndex((t) => t.id === thread.id);
  const next = {
    ...thread,
    updatedAt: new Date().toISOString(),
  };
  if (idx >= 0) list[idx] = next;
  else list.unshift(next);
  list.sort((a, b) => (b.updatedAt || "").localeCompare(a.updatedAt || ""));
  localStorage.setItem(CHATS_KEY, JSON.stringify(list.slice(0, 30)));
  setActiveChatId(next.id);
  return next;
}

export function deleteChatThread(id) {
  const list = loadChatThreads().filter((t) => t.id !== id);
  localStorage.setItem(CHATS_KEY, JSON.stringify(list));
  if (getActiveChatId() === id) setActiveChatId(list[0]?.id || null);
  return list;
}

export function newChatId() {
  return `chat_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`;
}
